import { useMemo } from "react";
import styled from "@emotion/styled";
import { Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import type { ContactDetailResponseDto } from "@shared/generated/crm/models/ContactDetailResponseDto";
import type { SidekickUpdateCard } from "../contacts.types";
import {
  buildNotableUpdatesCard,
  buildOpportunitiesCard,
} from "../signal-mappers";
import { SidekickUpdatesCard } from "./SidekickUpdatesCard";
import { OverviewCard } from "./OverviewCard";
import { useGetContactSummary } from "@shared/generated/crm/queries/contactsController/useGetContactSummary";
import { useContactPendingSuggestions } from "@features/Contacts/useContactPendingSuggestions";

const SectionTitle = styled(Typography)`
  color: ${(p) => p.theme.text.colors.brand};
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;

  ${(p) => p.theme.media.desktopDown} {
    grid-template-columns: 1fr 1fr;
  }

  ${(p) => p.theme.media.tabletDown} {
    grid-template-columns: 1fr;
  }
`;

type SidekickUpdatesCardsProps = {
  contact: ContactDetailResponseDto;
};

export const SidekickUpdatesCards = (props: SidekickUpdatesCardsProps) => {
  const { contact } = props || {};

  const { data: summaryData, isLoading: isSummaryLoading } =
    useGetContactSummary(contact.id);

  const { pendingSuggestionsCount } = useContactPendingSuggestions(contact);

  const hasPendingSuggestions = pendingSuggestionsCount > 0;

  const cards = useMemo(() => {
    return [
      buildNotableUpdatesCard(contact.contactFieldSignals || [], contact),
      buildOpportunitiesCard(contact.leads || []),
    ].filter((c): c is SidekickUpdateCard => c !== null);
  }, [contact]);

  const hasOverview = isSummaryLoading || !!summaryData?.summary;

  if (!hasOverview && cards.length === 0) return null;

  return (
    <section>
      <SectionTitle variant="subtitle1">
        <AutoAwesomeIcon fontSize="small" />
        Sidekick Updates
      </SectionTitle>
      <CardsGrid>
        {hasOverview && (
          <OverviewCard
            summary={summaryData?.summary}
            isLoading={isSummaryLoading}
          />
        )}
        {cards.map((card) => (
          <SidekickUpdatesCard
            title={card.title}
            content={card.content}
            leadGroups={card.leadGroups}
            hasPendingSuggestions={
              card.id === "notable-updates" ? hasPendingSuggestions : undefined
            }
            key={card.id}
          />
        ))}
      </CardsGrid>
    </section>
  );
};
